import { Box, Divider, IconButton, Stack, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { editProjectLanguages, editProjectMainLanguage, Project, qk } from 'api'
import { getLanguagesList } from 'api/languages'
import { IconEditPencil, IconTrashBin } from 'assets/icons'
import { CountryFlag } from 'components/global'
import { Dialog, DialogContent, ToastContent } from 'elements/feedback'
import { Button, Select } from 'elements/inputs'
import { getInputError } from 'lib/form-utils'
import { useBoolean } from 'lib/hooks'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { AddLanguagesForm } from './add-languages-form'

export const ProjectLanguagesTabContent = ({
  project,
}: {
  project: Project
}) => {
  const queryClient = useQueryClient()

  const { t } = useTranslation()

  const { languages, projectId, mainLanguage } = project

  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null)

  const addDialogOpen = useBoolean()
  const editMainLanguageDialog = useBoolean()

  const { control, handleSubmit } = useForm<{ mainLanguage: string }>({
    defaultValues: {
      mainLanguage,
    },
  })

  const closeDeleteDialog = () => {
    setSelectedLanguage(null)
  }

  const closeEditDialog = () => {
    editMainLanguageDialog.setFalse()
  }

  const $editProjectLanguages = useMutation(editProjectLanguages)
  const $editProjectMainLanguage = useMutation(editProjectMainLanguage)

  const { data: allLanguages, isLoading } = useQuery(
    qk.languages.list.toKey(),
    getLanguagesList,
  )

  return (
    <Box>
      <Stack
        width={1}
        mx="auto"
        bgcolor="common.white"
        border="1px solid"
        borderColor="primary.light"
        borderRadius="12px"
        p={3}
      >
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Stack>
            <Typography variant="caption2" fontWeight={600}>
              {t('projects.settings.add_new_languages')}
            </Typography>
            <Typography variant="caption3" color="text.secondary">
              {t('projects.settings.add_more_languages_text')}
            </Typography>
          </Stack>

          <Button
            variant="text"
            sx={{ color: 'primary.main' }}
            onClick={addDialogOpen.setTrue}
          >
            {`+ ${t('projects.settings.add_new_languages')}`}
          </Button>
        </Box>

        <Divider sx={{ borderBottomWidth: '1px' }} />

        <Stack divider={<Divider sx={{ borderBottomWidth: '1px' }} />}>
          {languages.map(languageCode => (
            <Box
              key={languageCode}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={1.5}
            >
              <Box display="flex">
                <CountryFlag languageCode={languageCode} size={24} />
                <Typography variant="caption2" fontWeight={600} ml={0.5}>
                  {t(`languages.${languageCode}`)}
                </Typography>
              </Box>

              {languageCode !== mainLanguage && (
                <IconButton
                  color="error"
                  onClick={() => {
                    setSelectedLanguage(languageCode)
                  }}
                >
                  <IconTrashBin />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>

        <Divider sx={{ borderBottomWidth: '1px' }} />
      </Stack>

      <Stack
        mt={6}
        width={1}
        mx="auto"
        bgcolor="common.white"
        border="1px solid"
        borderColor="primary.light"
        borderRadius="12px"
        p={3}
      >
        <Typography variant="caption2" fontWeight={600}>
          {t('projects.settings.change_primary_language')}
        </Typography>
        <Divider sx={{ borderBottomWidth: '1px', my: 1 }} />

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          py={1.5}
        >
          <Box display="flex">
            <CountryFlag languageCode={mainLanguage} size={24} />
            <Typography variant="caption2" fontWeight={600} ml={0.5}>
              {t(`languages.${mainLanguage}`)}
            </Typography>
          </Box>

          <IconButton color="error" onClick={editMainLanguageDialog.setTrue}>
            <IconEditPencil color="primary" />
          </IconButton>
        </Box>
      </Stack>

      <Dialog open={selectedLanguage !== null} onClose={closeDeleteDialog}>
        <DialogContent
          title={t('projects.settings.language_deletion_confirmation_title')}
          isActionLoading={$editProjectLanguages.isLoading}
          onConfirm={() => {
            $editProjectLanguages.mutate(
              {
                projectId,
                languages: languages.filter(
                  language => language !== selectedLanguage,
                ),
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries(
                    qk.projects.details.toKeyWithArgs({ projectId }),
                  )
                  closeDeleteDialog()
                  toast.success(
                    <ToastContent
                      title={t(
                        'projects.settings.language_successfully_removed',
                      )}
                    />,
                  )
                },
              },
            )
          }}
          onDeny={closeDeleteDialog}
          confirmText={t('common.delete')}
          denyText={t('common.cancel')}
          variant="danger"
        >
          <Typography variant="caption1" color="text.secondary">
            {t('projects.settings.language_deletion_confirmation_subtitle')}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={editMainLanguageDialog.isTrue} onClose={closeEditDialog}>
        <DialogContent
          title={t('projects.settings.change_primary_language')}
          isActionLoading={$editProjectMainLanguage.isLoading}
          onConfirm={handleSubmit(lang => {
            $editProjectMainLanguage.mutate(
              {
                projectId,
                ...lang,
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries(
                    qk.projects.details.toKeyWithArgs({ projectId }),
                  )
                  closeEditDialog()
                },
              },
            )
          })}
          onDeny={closeEditDialog}
          confirmText={t('common.save')}
          denyText={t('common.cancel')}
        >
          <Controller
            name="mainLanguage"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={t('projects.create.primary_language')}
                options={
                  allLanguages?.map(language => ({
                    value: language.code,
                    label: t(`languages.${language.code}`),
                  })) ?? []
                }
                {...getInputError(fieldState.error)}
                {...field}
              />
            )}
          />

          <Divider color="divider" sx={{ mt: 5, borderBottomWidth: 1 }} />
        </DialogContent>
      </Dialog>

      <Dialog open={addDialogOpen.isTrue} onClose={addDialogOpen.setFalse}>
        <AddLanguagesForm
          project={project}
          closeDialog={addDialogOpen.setFalse}
          languages={allLanguages}
          isLoading={isLoading}
        />
      </Dialog>
    </Box>
  )
}
